import { ACCESS_TOKEN_COOKIE, SESSION_ID_HEADER } from '@/constant/app'
import { isFsdsHostname, normalizeHostname } from '@/utils/domain-branding-resolver'

/**
 * Hostnames that are not platform subdomains (e.g. phongth.edunodes.tech) but may map to a tenant
 * via a custom domain record (e.g. robusto.ai → phongth).
 */
export function isResolvableCustomDomainHost(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname)
  if (!normalized || isFsdsHostname(normalized)) {
    return false
  }
  if (isIPv4Hostname(normalized)) {
    return false
  }
  if (isLocalTenantDevelopmentHost(normalized)) {
    return false
  }
  return getTenantFromHost(normalized) === 'default' && normalized.includes('.')
}

export function isCustomDomainLookupCandidate(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname)
  if (!normalized || isFsdsHostname(normalized) || isIPv4Hostname(normalized)) {
    return false
  }
  if (isLocalTenantDevelopmentHost(normalized)) {
    return false
  }
  if (
    normalized === EDUNODES_TENANT_DOMAIN ||
    normalized.endsWith(`.${EDUNODES_TENANT_DOMAIN}`)
  ) {
    return false
  }
  return normalized.includes('.')
}

export const EDUNODES_TENANT_DOMAIN = 'edunodes.tech'

export const FSDS_PLATFORM_DOMAIN = 'fullstackdatascience.com'

export function isFullstackdatascienceHost(
  hostname: string | null | undefined,
): boolean {
  const normalized = normalizeHostname(hostname)
  if (!normalized) {
    return false
  }
  return (
    normalized === FSDS_PLATFORM_DOMAIN ||
    normalized === `www.${FSDS_PLATFORM_DOMAIN}` ||
    normalized.endsWith(`.${FSDS_PLATFORM_DOMAIN}`)
  )
}

/**
 * Browser is on a community tenant site (not fsds platform fullstackdatascience.com).
 */
export function isTenantCommunityClientFromWindow(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const hostname = window.location.hostname
  if (isFullstackdatascienceHost(hostname)) {
    return false
  }
  const tenantSubdomain = getTenantFromWindow()
  if (!tenantSubdomain || tenantSubdomain === 'default') {
    return false
  }
  return (
    isEdunodesTenantHost(hostname) ||
    isResolvableCustomDomainHost(hostname) ||
    isLocalTenantDevelopmentHost(hostname)
  )
}

/** Edunodes account sync (signup check, sign-in join) — tenant client only, never fsds platform. */
export function shouldSyncEdunodesAccountFromWindow(): boolean {
  return (
    isTenantCommunityClientFromWindow() &&
    shouldShowEdunodesPoweredBy(window.location.hostname, {
      isFsdsDomain: isFsdsHostname(window.location.hostname),
      tenantSubdomain: getTenantFromWindow(),
    })
  )
}

/** Whether the current browser host is an Edunodes-powered community site. */
export function isEdunodesCommunitySiteFromWindow(): boolean {
  return shouldSyncEdunodesAccountFromWindow()
}

/** True for tenant community hosts such as acme.edunodes.tech. */
export function isEdunodesTenantHost(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname)
  if (!normalized) {
    return false
  }
  if (!normalized.endsWith(`.${EDUNODES_TENANT_DOMAIN}`)) {
    return false
  }
  return isTenantSubdomain(normalized)
}

/**
 * Show "Powered by Edunodes" on edunodes.tech subdomains and on tenant custom domains
 * (non-FSDS sites with a resolved community slug).
 */
export function shouldShowEdunodesPoweredBy(
  hostname: string | null | undefined,
  options: { isFsdsDomain: boolean; tenantSubdomain?: string | null },
): boolean {
  if (options.isFsdsDomain) {
    return false
  }

  const tenantSubdomain = options.tenantSubdomain?.trim().toLowerCase()
  if (!tenantSubdomain || tenantSubdomain === 'default') {
    return false
  }

  if (isEdunodesTenantHost(hostname)) {
    return true
  }

  const normalized = normalizeHostname(hostname)
  if (!normalized) {
    return false
  }

  return (
    getTenantFromHost(normalized) === 'default' && isResolvableCustomDomainHost(normalized)
  )
}

/** Strip auto-generated " Tenant" suffix from API tenant names for display/SEO. */
export function normalizeTenantDisplayName(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return trimmed
  return trimmed.replace(/\s+Tenant$/i, '').trim() || trimmed
}

const LOCAL_TENANT_DOMAINS = new Set(['local', 'localhost'])
const RESERVED_LOCAL_TENANT_PREFIXES = new Set(['api', 'app', 'admin', 'platform', 'www'])
/** Align with backend TenantContextMiddleware reserved subdomains */
const RESERVED_TENANT_SUBDOMAINS = new Set(['api', 'www', 'app', 'admin', 'platform'])

export function isIPv4Hostname(hostname: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
}

function getLocalTenantFromNormalizedHost(normalizedHostname: string): string | null {
  const parts = normalizedHostname.split('.')

  if (
    parts.length === 2 &&
    LOCAL_TENANT_DOMAINS.has(parts[1]) &&
    parts[0] &&
    !RESERVED_LOCAL_TENANT_PREFIXES.has(parts[0])
  ) {
    return parts[0]
  }

  return null
}

export function isLocalTenantDevelopmentHost(hostname: string | null | undefined): boolean {
  const normalizedHostname = normalizeHostname(hostname)
  return Boolean(normalizedHostname && getLocalTenantFromNormalizedHost(normalizedHostname))
}

/**
 * Extract tenant ID from hostname
 * Supports subdomain-based multi-tenancy (e.g., tenant1.yourdomain.com)
 */
export function getTenantFromHost(hostname: string | null | undefined): string {
  const normalizedHostname = normalizeHostname(hostname)

  if (!normalizedHostname || isIPv4Hostname(normalizedHostname)) {
    return 'default'
  }

  const localTenant = getLocalTenantFromNormalizedHost(normalizedHostname)

  if (localTenant) {
    return localTenant
  }

  const parts = normalizedHostname.split('.')

  // For subdomain.domain.com pattern
  // Check if we have at least 3 parts (subdomain.domain.com)
  if (parts.length >= 3) {
    const subdomain = parts[0]

    if (subdomain === 'www') {
      return 'default'
    }

    return subdomain
  }

  return 'default'
}

/**
 * Get tenant ID from current window location (client-side)
 */
export function getTenantFromWindow(): string {
  if (typeof window === 'undefined') {
    return 'default'
  }

  return getTenantFromHost(window.location.hostname)
}

/**
 * Check if current hostname is a tenant subdomain
 */
export function isTenantSubdomain(hostname: string | null | undefined): boolean {
  const tenant = getTenantFromHost(hostname)
  if (tenant === 'default') return false
  if (RESERVED_TENANT_SUBDOMAINS.has(tenant)) return false
  return true
}

/**
 * True when the app is served from fullstackdatascience.com (or a subdomain).
 * When false (e.g. phongth.edunodes.tech), session cookie is not in same-origin cookies,
 * so we must resolve session via API (getProfile with credentials) instead of /api/cookie.
 */
export function isOnFullstackdatascienceDomain(): boolean {
  if (typeof window === 'undefined') {
    return true
  }

  const hostname = window.location.hostname
  if (isTenantSubdomain(hostname)) return false

  return isFsdsHostname(hostname)
}

/**
 * AWS WebSocket private channel for the current user.
 * Legacy `user-{id}` on fullstackdatascience.com; `user-{tenant}-{id}` on other tenant domains.
 * Custom apex domains (e.g. robusto.ai) require `tenantSubdomain` from tenant context/API.
 */
export function buildUserWebSocketChannel(
  userId: number,
  hostname: string | null | undefined,
  tenantSubdomain?: string | null,
): string {
  if (!hostname || isFsdsHostname(hostname)) {
    return `user-${userId}`
  }

  const fromHost = getTenantFromHost(hostname)
  const tenant =
    fromHost !== 'default'
      ? fromHost
      : tenantSubdomain?.trim().toLowerCase() || 'default'

  if (tenant === 'default') {
    return `user-${userId}`
  }

  return `user-${tenant}-${userId}`
}

/**
 * Server-side: true when request host is fullstackdatascience.com (or its subdomain).
 * When false (e.g. phongth.edunodes.tech), the session cookie is not sent to this host,
 * so getServerSideProps cannot read it; auth is resolved client-side via API credentials.
 */
export function isOnFullstackdatascienceDomainFromRequest(host: string | null | undefined): boolean {
  if (!host) return true
  // Community tenant hosts (including phongth.fullstackdatascience.com) use JWT, not session SSR.
  if (isTenantSubdomain(host)) return false

  return isFsdsHostname(host)
}

/**
 * True when tenant config / media defaults should use fsds-be (main platform only).
 * Tenant subdomains and custom community domains use eztech-be instead.
 */
export function usesFsdsPlatformTenantConfig(hostname?: string | null): boolean {
  if (hostname) {
    return isOnFullstackdatascienceDomainFromRequest(hostname)
  }
  if (typeof window !== 'undefined') {
    return isOnFullstackdatascienceDomain()
  }
  return true
}

/**
 * Build an Origin header for server-side API calls so the backend tenant middleware
 * can resolve the client subdomain when the browser is not sending Origin (SSR/middleware).
 */
export function buildOriginHeaderForHost(
  host: string | null | undefined,
  protocol: 'http' | 'https' = 'https'
): string | undefined {
  const normalized = normalizeHostname(host)
  if (!normalized) return undefined
  return `${protocol}://${normalized}`
}

/**
 * Decide which auth mode to use:
 *   - fsds host  -> session cookie (works natively)
 *   - elsewhere  -> JWT bearer (cookie can't reach the browser due to RFC 6265
 *     domain-match + third-party cookie blocking)
 */
export function shouldUseJwtAuth(): boolean {
  if (typeof window === 'undefined') return false
  return !isFsdsHostname(window.location.hostname)
}

export function shouldUseJwtAuthForHost(host: string | null | undefined): boolean {
  if (!host) return false
  // Tenant subdomains (including phongth.localhost) always use JWT — session cookies
  // cannot reach the API host on cross-origin / custom-domain setups.
  if (isTenantSubdomain(host)) return true
  return !isFsdsHostname(host)
}

/**
 * True when the user should be sent to sign-in: main FSDS domain and no session cookie on this host.
 * On custom/tenant domains the API session cookie is not sent to Next.js, so never redirect here.
 */
export function missingSessionCookieRequiresSignIn(host: string | null | undefined, sessionCookie: string): boolean {
  return !sessionCookie && isOnFullstackdatascienceDomainFromRequest(host)
}

/**
 * SSR: redirect to sign-in when no credentials for the current host (session on FSDS, JWT on community).
 */
export function missingAuthRequiresSignIn(
  host: string | null | undefined,
  cookies: Partial<Record<string, string>> | Record<string, string | undefined>
): boolean {
  if (shouldUseJwtAuthForHost(host)) {
    const accessToken = cookies[ACCESS_TOKEN_COOKIE]?.trim()
    return !accessToken
  }
  const sessionCookie = cookies[SESSION_ID_HEADER]?.trim() ?? ''
  return missingSessionCookieRequiresSignIn(host, sessionCookie)
}

/** Sign-in path with optional return URL (`previousPage` query). */
export function getSignInRedirectDestination(locale?: string | null, returnPath?: string): string {
  const base = locale === 'en' ? '/en/sign-in' : '/sign-in'
  if (!returnPath) {
    return base
  }
  return `${base}?previousPage=${encodeURIComponent(returnPath)}`
}

/**
 * Build tenant URL from subdomain
 */
export function buildTenantUrl(subdomain: string, path = ''): string {
  if (typeof window === 'undefined') {
    // Server-side: use environment variable or default
    const baseDomain = process.env.NEXT_PUBLIC_MULTI_TENANT_DOMAIN || 'yourdomain.com'
    const protocol = process.env.NEXT_PUBLIC_PROTOCOL || 'https'
    return `${protocol}://${subdomain}.${baseDomain}${path}`
  }

  // Client-side: use current protocol and domain
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  if (parts.length >= 2) {
    // Extract base domain (e.g., 'yourdomain.com' from 'tenant1.yourdomain.com')
    const baseDomain = parts.slice(-2).join('.')
    return `${protocol}//${subdomain}.${baseDomain}${path}`
  }

  // Fallback for localhost
  return `${protocol}//${subdomain}.localhost${path}`
}

/**
 * Extract tenant ID from GetServerSidePropsContext
 * Used in getServerSideProps to get tenant from request headers
 */
export function getTenantFromRequest(req: {
  headers?: { host?: string }
  cookies?: Partial<Record<string, string>> | Record<string, string> | { [key: string]: string | undefined }
}): string {
  // First try to get from cookie (set by middleware)
  const tenantCookie = req.cookies?.tenant
  if (tenantCookie) {
    return tenantCookie
  }

  // Fallback: extract from host header
  const hostname = req.headers?.host
  return getTenantFromHost(hostname)
}
